<template>
  <div 
    class="comic-reader-container" 
    :class="{ 'is-vol-pay-locked': pagesLocked }"
    ref="readerContainer"
    @scroll="handleScroll"
    @click="onReaderChromeTapToggleDanmakuFab"
  >
    <Teleport to="body">
      <div v-if="isRestoringProgress" class="restore-progress-overlay">
        <div class="restore-progress-content">
          <div class="loading-spinner-large"></div>
        </div>
      </div>

      <ComicReaderToolbar
        :show="showTopBar"
        :volume-label="`第 ${currentDisplayVolNum || volNum} 卷`"
        @home="goToHome"
        @catalog="toggleCatalog"
      />

      <ComicReaderDanmakuBar
        v-model:show-danmaku-send-dialog="showDanmakuSendDialog"
        v-model:show-danmaku-settings-dialog="showDanmakuSettingsDialog"
        v-model:danmaku-draft="danmakuDraft"
        v-model:danmaku-text-opacity="danmakuTextOpacity"
        v-model:danmaku-display-area-percent="danmakuDisplayAreaPercent"
        v-model:danmaku-font-size-enum="danmakuFontSizeEnum"
        v-model:danmaku-speed-enum="danmakuSpeedEnum"
        :show-top-bar="showTopBar"
        :danmaku-enabled="danmakuEnabled"
        :danmaku-bar-ref="danmakuBarRef"
        :danmaku-interact-open="danmakuInteractOpen"
        :danmaku-effective-track-count="danmakuEffectiveTrackCount"
        :danmaku-bar-inner-area-style="danmakuBarInnerAreaStyle"
        :danmaku-row-indices="danmakuRowIndices"
        :danmaku-interact-content="danmakuInteractContent"
        :danmaku-interact-is-liked="danmakuInteractIsLiked"
        :danmaku-interact-liked-loading="danmakuInteractLikedLoading"
        :danmaku-interact-like-count="danmakuInteractLikeCount"
        :danmaku-fly-active="danmakuFlyActive"
        :danmaku-fly-playing="danmakuFlyPlaying"
        :danmaku-fly-text="danmakuFlyText"
        :danmaku-fly-vars="danmakuFlyVars"
        :danmaku-fab-visible="danmakuFabVisible"
        :danmaku-display-area-percent-clamped="danmakuDisplayAreaPercentClamped"
        :danmaku-line-scroll-style="danmakuLineScrollStyle"
        :danmaku-segments-for-track="danmakuSegmentsForTrack"
        :danmaku-item-inline-style="danmakuItemInlineStyle"
        :danmaku-display-area-slider-tooltip="danmakuDisplayAreaSliderTooltip"
        :danmaku-font-size-slider-tooltip="danmakuFontSizeSliderTooltip"
        :danmaku-speed-slider-tooltip="danmakuSpeedSliderTooltip"
        :danmaku-font-size-enum-label="danmakuFontSizeEnumLabel"
        :danmaku-speed-enum-label="danmakuSpeedEnumLabel"
        :on-danmaku-segment-click="onDanmakuSegmentClick"
        :close-danmaku-interact="closeDanmakuInteract"
        :toggle-danmaku-interact-like="toggleDanmakuInteractLike"
        :toggle-danmaku-visibility-fab="toggleDanmakuVisibilityFab"
        :open-danmaku-send-dialog="openDanmakuSendDialog"
        :on-danmaku-settings-opened="onDanmakuSettingsOpened"
        :on-danmaku-settings-closed="onDanmakuSettingsClosed"
        :submit-danmaku-send="submitDanmakuSend"
        :on-danmaku-fly-animation-end="onDanmakuFlyAnimationEnd"
      />

      <ComicReaderCatalog
        :show="showCatalog"
        :loading="isLoadingCatalog"
        :catalog-data="catalogData"
        :active-vol-num="getCatalogActiveVolNum()"
        :get-vol-num="getVolNumFromTitle"
        :format-date="formatDate"
        :get-progress-offset="getProgressOffset"
        :catalog-content-ref="catalogContentRef"
        @close="closeCatalog"
        @select="goToVolume"
      />
    </Teleport>

    <ComicReaderPageList
      v-model:comment-input="commentInput"
      :pages="pages"
      :vol-num="volNum"
      :interact-data="interactData"
      :loaded-prev-volumes="loadedPrevVolumes"
      :loaded-volumes="loadedVolumes"
      :next-volume-num="nextVolumeNum"
      :show-load-next-volume-prompt="showLoadNextVolumePrompt"
      :show-load-prev-volume-prompt="showLoadPrevVolumePrompt"
      :load-volume-prompt-text="loadVolumePromptText"
      :is-loading-prev-volume="isLoadingPrevVolume"
      :comment-el-id="readerCommentElId"
      :set-image-ref="setImageRef"
      :set-loaded-volume-image-ref="setLoadedVolumeImageRef"
      :set-next-volume-image-ref="setNextVolumeImageRef"
      :get-current-volume-images="getCurrentVolumeImages"
      :get-current-volume-start-index="getCurrentVolumeStartIndex"
      :should-show-next-volume-loading="shouldShowNextVolumeLoading"
      :get-loading-next-volume-label="getLoadingNextVolumeLabel"
      :get-avatar-url="getAvatarUrl"
      :get-comment-avatar-url="getCommentAvatarUrl"
      :format-comment-time="formatCommentTime"
      :handle-reward="handleReward"
      :handle-like="handleLike"
      :handle-collect="handleCollect"
      :submit-comment="submitComment"
      :handle-comment-like="handleCommentLike"
      :handle-loaded-volume-like="handleLoadedVolumeLike"
      :handle-loaded-volume-collect="handleLoadedVolumeCollect"
      :submit-loaded-volume-comment="submitLoadedVolumeComment"
      :handle-loaded-volume-comment-like="handleLoadedVolumeCommentLike"
      :load-more-comments="loadMoreComments"
      :collapse-comments="collapseComments"
      :is-loading-more-comments="isLoadingMoreComments"
      :handle-avatar-error="handleAvatarError"
      :handle-comment-avatar-error="handleCommentAvatarError"
      :reply-content-map="replyContentMap"
      :show-reply-box-map="showReplyBoxMap"
      :submitting-reply-map="submittingReplyMap"
      :pending-expand-replies-map="pendingExpandRepliesMap"
      :show-all-replies-map="showAllRepliesMap"
      :toggle-reply-box="toggleReplyBox"
      :submit-vol-comment-reply="submitVolCommentReply"
      :toggle-vol-comment-replies="toggleVolCommentReplies"
      :toggle-show-all-replies="toggleShowAllReplies"
      :vol-comment-has-replies="volCommentHasReplies"
      :delete-vol-comment="deleteVolComment"
      :delete-vol-comment-reply="deleteVolCommentReply"
    />
  </div>
</template>

<script setup>
import { toRef, watch } from 'vue'
import ComicReaderToolbar from '@/features/comic/components/ComicReaderToolbar.vue'
import ComicReaderDanmakuBar from '@/features/comic/components/ComicReaderDanmakuBar.vue'
import ComicReaderCatalog from '@/features/comic/components/ComicReaderCatalog.vue'
import ComicReaderPageList from '@/features/comic/components/ComicReaderPageList.vue'
import { useComicReaderPage } from '@/features/comic/composables/useComicReaderPage'
import { useVolPay } from '@/features/comic/composables/useVolPay'

const props = defineProps({
  comicId: { type: [String, Number], required: true },
  volNum: { type: [String, Number], required: true },
})

const emit = defineEmits(['toggle-nav-footer', 'show-popup-message', 'show-login-popup'])

const {
  pages,
  token,
  imageRefs,
  observer,
  lastImageObserver,
  currentPageIndex,
  maxSavedPageIndex,
  saveProgressTimer,
  isRestoringProgress,
  progressKey,
  lastScrollTop,
  scrollTimer,
  isMobile,
  showTopBar,
  showCatalog,
  totalVolumes,
  volumeList,
  volInfoVos,
  catalogData,
  isLoadingCatalog,
  currentPageNum,
  pageSize,
  totalPages,
  totalCount,
  hasNext,
  hasPrev,
  isLoading,
  pagesLocked,
  loadNextPageThreshold,
  lastLoadTriggerIndex,
  volId,
  interactData,
  isLoadingInteract,
  currentUser,
  commentInput,
  nextVolumePages,
  isLoadingNextVolume,
  nextVolumeNum,
  nextVolumeVolId,
  nextVolumeCurrentPage,
  nextVolumeHasNext,
  nextVolumeLastLoadTriggerIndex,
  nextVolumeInteractData,
  isLoadingNextVolumeInteract,
  nextVolumeCommentInput,
  loadedVolumes,
  isCheckingNextVolume,
  isCurrentVolumeComplete,
  showLoadNextVolumePrompt,
  scrollTriggeredLoadNext,
  prevVolumePages,
  isLoadingPrevVolume,
  prevVolumeNum,
  prevVolumeVolId,
  prevVolumeCurrentPage,
  prevVolumeHasNext,
  prevVolumeInteractData,
  prevVolumeCommentInput,
  loadedPrevVolumes,
  showLoadPrevVolumePrompt,
  scrollTriggeredLoadPrev,
  loadVolumePromptText,
  currentDisplayVolNum,
  danmakuList,
  danmakuEnabled,
  danmakuTextOpacity,
  danmakuDurationMin,
  danmakuDurationMax,
  danmakuFontSizeEnum,
  danmakuSpeedEnum,
  danmakuDisplayAreaPercent,
  showDanmakuSendDialog,
  showDanmakuSettingsDialog,
  danmakuDraft,
  danmakuHighlightClearTimer,
  danmakuFlyActive,
  danmakuFlyPlaying,
  danmakuFlyText,
  danmakuFlyVars,
  danmakuPendingFly,
  danmakuLaunchLoading,
  danmakuLaunchLoaded,
  danmakuInteractOpen,
  danmakuInteractDanmakuId,
  danmakuInteractContent,
  danmakuInteractLikeCount,
  danmakuInteractIsLiked,
  danmakuInteractLikedLoading,
  danmakuInteractAutoCloseTimer,
  _danmakuSettingsSnapshot,
  danmakuFabVisible,
  readerContainer,
  catalogContentRef,
  danmakuBarRef,
  readingProgress,
  danmakuDisplayItems,
  danmakuFontSizePx,
  danmakuScrollDurationSec,
  danmakuDisplayAreaPercentClamped,
  danmakuRowCount,
  danmakuConfiguredTrackCount,
  danmakuEffectiveTrackCount,
  danmakuBarInnerAreaStyle,
  danmakuRowIndices,
  updateIsMobile,
  setImageRef,
  setLoadedVolumeImageRef,
  fetchComicPages,
  initProgressTracking,
  setupLastImageObserver,
  checkAndLoadNextVolume,
  updateCurrentPage,
  loadNextPage,
  debouncedSaveProgress,
  saveProgress,
  syncProgressToServer,
  restoreProgress,
  waitForImagesLoaded,
  prevVolume,
  nextVolume,
  resetScrollPosition,
  handleVisibilityChange,
  handleBeforeUnload,
  detectCurrentDisplayVolume,
  handleScroll,
  fetchTotalVolumes,
  getVolIdFromVolInfoVos,
  redirectToTargetVolIfNeeded,
  readerCommentElId,
  tryScrollToHighlightedVolComment,
  fetchInteractData,
  checkCurrentVolumeComplete,
  fetchCurrentUser,
  handleLike,
  handleCollect,
  handleReward,
  submitComment,
  handleCommentLike,
  handleLoadedVolumeCommentLike,
  loadMoreComments,
  collapseComments,
  isLoadingMoreComments,
  getAvatarUrl,
  getCommentAvatarUrl,
  handleAvatarError,
  handleCommentAvatarError,
  formatCommentTime,
  goToHome,
  toggleCatalog,
  fetchCatalog,
  closeCatalog,
  bootstrapDanmakuUserConf,
  fetchDanmakuUserConfFromServer,
  applyDanmakuConfFromApi,
  danmakuFontEnumToPx,
  migratePxToFontEnum,
  danmakuSpeedEnumToDurationSec,
  migrateDurationToSpeedEnum,
  danmakuFontSizeEnumLabel,
  danmakuSpeedEnumLabel,
  danmakuFontSizeSliderTooltip,
  danmakuSpeedSliderTooltip,
  danmakuDisplayAreaSliderTooltip,
  buildDanmakuConfUpdatePayload,
  saveDanmakuUserConfRemote,
  toggleDanmakuVisibilityFab,
  onReaderChromeTapToggleDanmakuFab,
  onDanmakuSettingsOpened,
  onDanmakuSettingsClosed,
  loadDanmakuSettings,
  saveDanmakuSettings,
  fetchDanmakuLaunchQuery,
  extractDanmakuLaunchPageContents,
  normalizeDanmakuRowIdFromPayload,
  applyDanmakuLaunchContents,
  clearDanmakuInteractAutoCloseTimer,
  scheduleDanmakuInteractAutoClose,
  closeDanmakuInteract,
  onDanmakuSegmentClick,
  toggleDanmakuInteractLike,
  openDanmakuSendDialog,
  danmakuSegmentsForTrack,
  danmakuLineScrollStyle,
  danmakuItemInlineStyle,
  submitDanmakuSend,
  onDanmakuFlyAnimationEnd,
  danmakuAfterFlySendAndRefresh,
  goToVolume,
  getVolNumFromTitle,
  formatDate,
  getProgressOffset,
  getCatalogActiveVolNum,
  loadNextVolumeAfterScroll,
  loadNextVolumeAllPages,
  fetchLoadedVolumeInteractData,
  loadPrevVolumeAfterScroll,
  loadPrevVolumeAllPages,
  loadNextVolume,
  loadNextVolumeNextPage,
  getNextVolumeVolIdAndFetchInteract,
  fetchNextVolumeInteractData,
  loadNextVolumeAfterCurrent,
  setNextVolumeImageRef,
  getCurrentVolumeImages,
  getCurrentVolumeStartIndex,
  shouldShowNextVolumeLoading,
  getLoadingNextVolumeLabel,
  handleLoadedVolumeLike,
  handleLoadedVolumeCollect,
  submitLoadedVolumeComment,
  replyContentMap,
  showReplyBoxMap,
  submittingReplyMap,
  pendingExpandRepliesMap,
  showAllRepliesMap,
  toggleReplyBox,
  submitVolCommentReply,
  toggleVolCommentReplies,
  toggleShowAllReplies,
  volCommentHasReplies,
  deleteVolComment,
  deleteVolCommentReply,
} = useComicReaderPage(toRef(props, 'comicId'), toRef(props, 'volNum'), emit)

const { visible: volPayVisible } = useVolPay()

watch(volPayVisible, (open) => {
  if (open) showTopBar.value = true
})

watch(pagesLocked, (locked) => {
  if (locked) {
    showTopBar.value = true
    if (readerContainer.value) readerContainer.value.scrollTop = 0
  }
})
</script>

<style src="@/features/comic/styles/ComicReader.css"></style>
<style src="@/features/comic/styles/ComicReaderDanmakuDialogs.css"></style>
